import {
  createOrUpdatePluginInstance,
  disconnectPlugin,
  getPluginCode,
  getPluginDetails,
  listPlugins,
  managePlugin,
} from "@fuse/api/pluginApi"

import { getEffectivePluginConfig } from "@settingsAlias/pluginDefaults"

import { analyzeHostConfigForZeroConfig } from "@fuse/utils/examplePluginConverter"
import { convertApiPluginToInternal } from "@fuse/utils/pluginConverters"

import { Plugin, PluginFile, UserRole } from "../types"

interface PluginDetailsResponse {
  plugin: any
  configuration?: any
}

/**
 * Fetches the list of plugins from the API
 * @returns Promise with the result of the plugin list operation
 */
export const fetchPluginList = async () => {
  try {
    // Import the listPlugins function dynamically to avoid circular dependencies
    const { listPlugins } = await import("@fuse/api/pluginApi")
    const result = await listPlugins()

    if (!result.success) {
      return { success: false, error: result.error, plugins: [] }
    }

    return {
      success: true,
      plugins: result.data?.plugins || [],
    }
  } catch (error) {
    console.error("Error fetching plugin list:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      plugins: [],
    }
  }
}

/**
 * Fetches a plugin's details from the API
 * @param pluginId - ID of the plugin to fetch
 * @param eventId - Optional event ID to get instance-specific data
 * @returns Promise with plugin details and success status
 */
export const fetchPluginDetails = async (
  pluginId: string,
  eventId?: string,
) => {
  try {
    const result = await getPluginDetails({ pluginId, eventId })

    if (!result.success || !result.data) {
      console.error(
        "[pluginApiEffects] Error fetching plugin details:",
        result.error,
      )
      return { success: false, plugin: null, error: result.error }
    }

    const responseData = result.data as PluginDetailsResponse
    if (!responseData.plugin) {
      return {
        success: false,
        plugin: null,
        error: "Plugin not found in response",
      }
    }

    return {
      success: true,
      plugin: responseData.plugin,
      configuration: responseData.configuration,
    }
  } catch (error) {
    console.error(
      "[pluginApiEffects] Exception fetching plugin details:",
      error,
    )
    return {
      success: false,
      plugin: null,
      error: error instanceof Error ? error.message : "Unknown error",
    }
  }
}

/**
 * Determines if a plugin is from an example (based on naming patterns)
 */
const isExamplePlugin = (plugin: Plugin): boolean => {
  // Check if it's a new plugin with example-like characteristics
  const isNewPlugin = plugin.isNew || plugin.id.includes("new-plugin-")
  const hasExampleAuthor = plugin.manifest?.author === "Plugin Studio User"
  const hasDefaultVersion = plugin.manifest?.version === "1.0.0"

  return isNewPlugin && hasExampleAuthor && hasDefaultVersion
}

/**
 * Saves a plugin to the API
 * @param plugin - Plugin data to save
 * @param operation - Operation to perform (create/update/publish)
 * @returns Promise with the result of the save operation
 */
export const savePlugin = async (
  plugin: Plugin,
  operation: "create" | "update" | "publish" = "update",
) => {
  try {
    const config = getEffectivePluginConfig()

    // Format files content for API
    const guestFile = plugin.files.find((f) => f.type === UserRole.GUEST)
    const hostEventViewFile = plugin.files.find(
      (f) => f.type === UserRole.HOST_EVENT_VIEW,
    )
    const hostConfigFile = plugin.files.find(
      (f) => f.type === UserRole.HOST_CONFIG_VIEW,
    )

    // Determine if we should apply auto-detection
    const isExample = isExamplePlugin(plugin)
    const shouldApplyAutoDetection =
      config.APPLY_AUTO_DETECTION_TO_ALL_PLUGINS || isExample

    // Analyze host config for zero-config detection
    const hostConfigContent = hostConfigFile?.content || ""
    const isZeroConfigDetected =
      config.ENABLE_AUTO_ZERO_CONFIG_DETECTION && shouldApplyAutoDetection
        ? analyzeHostConfigForZeroConfig(hostConfigContent)
        : false

    // Determine final manifest with auto-detected values
    const baseManifest = plugin.manifest || {
      name: plugin.name || "Untitled Plugin",
      description: plugin.description || "",
      version: "0.1.0",
      author: "",
    }

    // Apply auto-detection logic based on configuration
    let finalRequiresZeroConfig = baseManifest.requiresZeroConfiguration
    let finalApprovalStatus = baseManifest.approvalStatus

    // Auto-set zero-config if not explicitly set
    if (finalRequiresZeroConfig === undefined && shouldApplyAutoDetection) {
      if (isExample && config.AUTO_MARK_EXAMPLES_AS_ZERO_CONFIG) {
        finalRequiresZeroConfig = true
      } else if (isZeroConfigDetected) {
        finalRequiresZeroConfig = true
      }
    }

    // Auto-set approval status for new plugins if not set
    if (!finalApprovalStatus && operation === "create") {
      finalApprovalStatus = config.DEFAULT_APPROVAL_STATUS
    }

    const finalManifest = {
      ...baseManifest,
      requiresZeroConfiguration: finalRequiresZeroConfig,
      approvalStatus: finalApprovalStatus,
    }

    console.log(`[savePlugin] Auto-detection for ${plugin.name}:`, {
      operation,
      isExample,
      shouldApplyAutoDetection,
      isZeroConfigDetected,
      originalRequiresZeroConfig: baseManifest.requiresZeroConfiguration,
      finalRequiresZeroConfig,
      originalApprovalStatus: baseManifest.approvalStatus,
      finalApprovalStatus,
      hostConfigLength: hostConfigContent.length,
      configSettings: {
        autoMarkExamples: config.AUTO_MARK_EXAMPLES_AS_ZERO_CONFIG,
        enableAutoDetection: config.ENABLE_AUTO_ZERO_CONFIG_DETECTION,
        applyToAllPlugins: config.APPLY_AUTO_DETECTION_TO_ALL_PLUGINS,
        defaultApprovalStatus: config.DEFAULT_APPROVAL_STATUS,
      },
    })

    // Prepare code object for the API
    const code = {
      frontendGuestCodeWeb: guestFile?.content || "",
      frontendHostViewCodeWeb: hostEventViewFile?.content || "",
      frontendHostConfigCodeWeb: hostConfigFile?.content || "",
      transpiledFrontendGuestCodeWeb: guestFile?.transpiledContent || "",
      transpiledFrontendHostViewCodeWeb:
        hostEventViewFile?.transpiledContent || "",
      transpiledFrontendHostConfigCodeWeb:
        hostConfigFile?.transpiledContent || "",
    }

    // Call API to save the plugin
    const result = await managePlugin({
      operation,
      manifest: finalManifest,
      code,
      // Only include pluginId for update operations
      pluginId: operation === "update" ? plugin.id : undefined,
    })

    if (!result.success) {
      return { success: false, error: result.error }
    }

    // For create operations, ensure we return the new plugin ID
    if (operation === "create" && result.data) {
      const data = result.data as { pluginId?: string }
      if (!data.pluginId) {
        return {
          success: false,
          error: "Server did not return a plugin ID for create operation",
        }
      }
    }

    return { success: true, data: result.data }
  } catch (error) {
    console.error(`Error during ${operation} plugin operation:`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    }
  }
}

/**
 * Updates a plugin instance for an event
 * @param eventId - The event ID
 * @param pluginId - The plugin ID
 * @param configuration - The plugin configuration
 * @param enabled - Whether the plugin is enabled
 * @returns Promise with the result of the update operation
 */
export const updatePluginInstance = async (
  eventId: string,
  pluginId: string,
  configuration: any,
  enabled: boolean,
) => {
  try {
    const result = await createOrUpdatePluginInstance({
      eventId,
      pluginId,
      configuration,
      enabled,
    })

    return result
  } catch (error) {
    console.error("[pluginApiEffects] Error updating plugin instance:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    }
  }
}
